import { List } from 'immutable'
import { useDispatch, useSelector } from 'react-redux'
import React, { useState } from 'react'
import FormsyForm from 'formsy-react'
import {
  selectEmailAvailabilityProcessing,
  selectEmailAvailability,
  selectHasEmailAvailable,
  selectHasChildProfile,
  selectUserData,
} from '../../../services/user/selectors'
import {
  resetUserEmailAvailability,
  getUserEmailAvailability,
  resetUserChildAccount,
} from '../../../services/user/actions'
import { selectGiftRedeemProcessing } from '../../../services/gift/selectors'
import { URL_TERMS_PRIVACY } from '../../../services/url/constants'
import { getPasswordValidation } from '../../../services/password/index'
import { TARGET_BLANK } from '../../Link/constants'
import { useTranslation } from '../../../services/static-text/hooks'
import { giftRedeem } from '../../../services/gift/actions'
import Button, { TYPES } from '../../Button/index'
import { H6 } from '../../Heading/index'
import Link from '../../Link/index'
import { EN } from '../../../services/languages/constants'
import {
  FORM_INPUT_NAME_EMAIL_OPT_IN,
  FORM_INPUT_NAME_FIRST_NAME,
  FORM_INPUT_NAME_LAST_NAME,
  FORM_INPUT_NAME_PASSWORD,
  FORM_INPUT_NAME_EMAIL,
  FORM_INPUT_NAME_TERMS,
} from '../../../services/form/index'
import {
  CHECKBOX_STYLES,
  EmailInput,
  TextInput,
  Password,
  Checkbox,
} from '../../FormInput.v2/index'

function renderPrivacyTerms (translateByKey, location = {}) {
  // use query to retain language when opening link in a new tab
  const { query } = location
  return (
    <span>
      {`${translateByKey('agree1')} ` }
      <Link
        directLink
        to={URL_TERMS_PRIVACY}
        query={query}
        target={TARGET_BLANK}
        tabIndex="8"
      >
        {translateByKey('agree2')}
      </Link>
      {` ${translateByKey('agree3')}`}
    </span>
  )
}

export default function ModalGiftConfirm ({ location }) {
  //
  const emailAvailabilityProcessing = useSelector(selectEmailAvailabilityProcessing)
  const emailAvailability = useSelector(selectEmailAvailability)
  const hasEmailAvailable = useSelector(selectHasEmailAvailable)
  const hasChildProfile = useSelector(selectHasChildProfile)
  const translateByKey = useTranslation('modalGiftCreateAccount')
  const redeemProcessing = useSelector(selectGiftRedeemProcessing)
  const userData = useSelector(selectUserData)
  const userLanguage = userData.get('language', List([EN])) || List([EN])
  const [emailValue, setEmailValue] = useState('')
  const [onBlurEmailValue, setOnBlurEmailValue] = useState(null)
  const [emailOptInValue, setEmailOptInValue] = useState(false)
  const [termsInitialChecked, setTermsInitialChecked] = useState(false)
  const [termsValue, setTermsValue] = useState(false)
  const [validForm, setValidForm] = useState(false)
  const dispatch = useDispatch()
  const submit = (model) => {
    dispatch(giftRedeem({ ...model, language: userLanguage.toJS() }))
  }
  const onValid = () => setValidForm(true)
  const onInvalid = () => setValidForm(false)

  const handleOnChangeEmailOptIn = (name, checked) => {
    setEmailOptInValue(checked)
  }

  const handleOnChangeTerms = (name, checked) => {
    setTermsValue(checked)
    if (!checked) setValidForm(true)
    if ((!termsInitialChecked && checked) || (!termsInitialChecked && !checked)) {
      setTermsInitialChecked(true)
    }
  }

  const getForcedEmailError = () => {
    let forcedError = ''
    if (emailAvailability === false) {
      forcedError = 'isEmailAvailable'
    } else if (hasEmailAvailable && (emailAvailability === null)) {
      forcedError = 'isEmailCaptureValid'
    }
    return forcedError
  }

  function handleOnChangeEmail (value) {
    setEmailValue(value)
    if (hasEmailAvailable) {
      dispatch(resetUserEmailAvailability())
    }
    if (hasChildProfile) {
      dispatch(resetUserChildAccount())
    }
  }

  function handleOnBlurCheckEmail (value) {
    const pattern = /^.+@.+\..{2,}$/
    // We are compensating for Formsy saying the email is valid (when its not)
    // and if the email value equals what has already been checked, then bail
    if (!pattern.test(value) || value === onBlurEmailValue) {
      return
    }
    dispatch(getUserEmailAvailability(value))
    setOnBlurEmailValue(value)
  }

  return (
    <div className="modal-gift-create-account">
      <div className="modal-gift-create-account__wrapper">
        <H6>{translateByKey('createAccount')}</H6>
        <FormsyForm
          className="modal-gift-create-account__form"
          onValidSubmit={submit}
          onInvalid={onInvalid}
          onValid={onValid}
        >
          <TextInput
            validationErrors={{
              isSpecialWords: translateByKey('errorFirstName'),
            }}
            validations={{ isSpecialWords: true }}
            label={translateByKey('firstName')}
            name={FORM_INPUT_NAME_FIRST_NAME}
            validationTimeout={750}
            maxLength={32}
            tabIndex="1"
            value=""
            required
          />
          <TextInput
            validationErrors={{
              isSpecialWords: translateByKey('errorLastName'),
            }}
            validations={{ isSpecialWords: true }}
            label={translateByKey('lastName')}
            name={FORM_INPUT_NAME_LAST_NAME}
            validationTimeout={750}
            maxLength={32}
            tabIndex="2"
            value=""
            required
          />
          <EmailInput
            validationErrors={{
              isEmail: translateByKey('emailValidError'),
              isEmailAvailable: translateByKey('emailAvailableError'),
              isEmailCaptureValid: translateByKey('errorMessageInvalidEmail'),
            }}
            className="modal-gift-create-account__email"
            disabled={emailAvailabilityProcessing}
            validations={{
              isEmail: true,
            }}
            forceError={getForcedEmailError()}
            label={translateByKey('email')}
            onBlur={handleOnBlurCheckEmail}
            onChange={handleOnChangeEmail}
            name={FORM_INPUT_NAME_EMAIL}
            validationTimeout={750}
            value={emailValue}
            tabIndex="3"
            required
          />
          <Password
            validationErrors={{
              maxLength: translateByKey('passwordMaxLengthMessage'),
              matchRegexp: translateByKey('passwordError'),
              minLength: translateByKey('passwordError'),
            }}
            className="modal-gift-create-account__password"
            validations={getPasswordValidation()}
            label={translateByKey('password')}
            name={FORM_INPUT_NAME_PASSWORD}
            showStrength
            tabIndex="4"
            value=""
            required
          />
          <Checkbox
              validationErrors={{
                equals: termsInitialChecked ? 'requiredField' : null,
              }}
              label={renderPrivacyTerms(translateByKey, location)}
              className="modal-gift-create-account__terms-optin"
              style={CHECKBOX_STYLES.PRIMARY}
              onChange={handleOnChangeTerms}
              validations={{ equals: true }}
              name={FORM_INPUT_NAME_TERMS}
              value={termsValue}
              setValueOnKeyUp
              tabIndex="5"
              required
            />
            <Checkbox
              className="modal-gift-create-account__email-optin"
              label={translateByKey('emailOptInLabel')}
              name={FORM_INPUT_NAME_EMAIL_OPT_IN}
              onChange={handleOnChangeEmailOptIn}
              value={emailOptInValue}
              setValueOnKeyUp
              tabIndex="6"
            />
          <Button
            className="modal-gift-create-account__btn"
            disabled={!validForm
              || redeemProcessing
              || !hasEmailAvailable
              || !!getForcedEmailError()}
            type={TYPES.PRIMARY}
            submit
          >
            {redeemProcessing
              ? translateByKey('processing')
              : translateByKey('redeemBtn')}
          </Button>
        </FormsyForm>
      </div>
    </div>
  )
}
